import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { callMethod } from '../../../common/utilsClient/ddp/actions';
import { apiZedocAllApointments } from '../../../common/api/zedoc';

/**
 * Custom hook to fetch appointments/visits from OMOP database
 *
 * @param {Object} params - { page: number, pageSize: number, status: string, patientName: string, clinicianId: number, startDate: string, endDate: string }
 * @returns {Object} { appointments: Array, total: number, loading: boolean, error: Error | null, refetch: Function }
 */
export const useAppointments = ({
  page = 1,
  pageSize = 10,
  status,
  patientName,
  clinicianId,
  startDate,
  endDate,
  skip = false,
} = {}) => {
  const dispatch = useDispatch();
  const [appointments, setAppointments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch appointments whenever page, pageSize, status, patientName, clinicianId, or date range changes
  useEffect(() => {
    if (skip) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    const apiParams = { page, pageSize };
    if (status) {
      apiParams.status = status;
    }
    if (patientName) {
      apiParams.patientName = patientName;
    }
    if (clinicianId) {
      apiParams.clinicianId = clinicianId;
    }
    if (startDate) {
      apiParams.startDate = startDate;
    }
    if (endDate) {
      apiParams.endDate = endDate;
    }

    dispatch(callMethod(apiZedocAllApointments, apiParams))
      .then((result) => {
        setLoading(false);
        // Handle both old array format and new {data, total} format for backwards compatibility
        if (result && typeof result === 'object' && 'data' in result) {
          setAppointments(result.data || []);
          setTotal(result.total || 0);
        } else {
          setAppointments(result || []);
          setTotal((result || []).length);
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error('Error fetching appointments:', err);
        setError(err);
      });
  }, [
    dispatch,
    page,
    pageSize,
    status,
    patientName,
    clinicianId,
    startDate,
    endDate,
    skip,
  ]);

  const refetch = useCallback(() => {
    // Manually refetch with current params
    setLoading(true);
    setError(null);

    const apiParams = { page, pageSize };
    if (status) {
      apiParams.status = status;
    }
    if (patientName) {
      apiParams.patientName = patientName;
    }
    if (clinicianId) {
      apiParams.clinicianId = clinicianId;
    }
    if (startDate) {
      apiParams.startDate = startDate;
    }
    if (endDate) {
      apiParams.endDate = endDate;
    }

    dispatch(callMethod(apiZedocAllApointments, apiParams))
      .then((result) => {
        setLoading(false);
        if (result && typeof result === 'object' && 'data' in result) {
          setAppointments(result.data || []);
          setTotal(result.total || 0);
        } else {
          setAppointments(result || []);
          setTotal((result || []).length);
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error('Error fetching appointments:', err);
        setError(err);
      });
  }, [
    dispatch,
    page,
    pageSize,
    status,
    patientName,
    clinicianId,
    startDate,
    endDate,
  ]);

  return {
    appointments,
    total,
    loading,
    error,
    refetch,
  };
};

/**
 * Alternative implementation using Tracker (reactive)
 * Subscribes to omop.visits publication for real-time updates
 */
// export const useAppointmentsReactive = (filters = {}) => {
//   const { appointments, loading, error } = useTracker(() => {
//     const handle = Meteor.subscribe('omop.visits', filters);

//     if (!handle.ready()) {
//       return { appointments: [], loading: true, error: null };
//     }

//     // Use method to get transformed data
//     // Note: This is called reactively, but methods are not reactive by default
//     // For true reactivity, you'd need to fetch from minimongo collections directly
//     return { appointments: [], loading: false, error: null };
//   }, [JSON.stringify(filters)]);

//   return { appointments, loading, error };
// };

export default useAppointments;
